import React, { useCallback, useEffect, useRef, useState } from "react";
import { Activity, RefreshCw, ShieldCheck } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axiosClient from "../../api/axiosClient";
import { toast } from "react-toastify";

dayjs.extend(relativeTime);

const QUALITIES = {
  GREEN: { color: "text-emerald-600 bg-emerald-50 border-emerald-100", label: "High", dot: "bg-emerald-500" },
  YELLOW: { color: "text-amber-600 bg-amber-50 border-amber-100", label: "Medium", dot: "bg-amber-500" },
  RED: { color: "text-rose-600 bg-rose-50 border-rose-100", label: "Low", dot: "bg-rose-500" },
  NA: { color: "text-slate-500 bg-slate-50 border-slate-100", label: "—", dot: "bg-slate-400" },
  UNKNOWN: { color: "text-slate-500 bg-slate-50 border-slate-100", label: "—", dot: "bg-slate-400" },
};

const CONNECTED_NUMBER_GRID =
  "lg:grid-cols-[minmax(145px,160px)_minmax(200px,1fr)_110px_70px_115px_75px]";

const TIERS = {
  TIER_NOT_VERIFIED: "Trial (50/day)",
  TIER_250: "250/day",
  TIER_2K: "2,000/day",
  TIER_1K: "1K/day",
  TIER_10K: "10K/day",
  TIER_100K: "100K/day",
  TIER_UNLIMITED: "Unlimited",
  UNTIERED: "Not tiered",
};

const formatMessagingLimitTier = value => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const normalized = raw.toUpperCase();
  if (TIERS[normalized]) return TIERS[normalized];
  const numeric = Number(raw.replace(/,/g, ""));
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric >= 1000000
      ? "Unlimited"
      : `${new Intl.NumberFormat("en-IN").format(numeric)}/day`;
  }
  return raw;
};

const readField = (row, camelName, pascalName) =>
  row?.[camelName] ?? row?.[pascalName];

const normalizeConnectedNumbers = rows =>
  (Array.isArray(rows) ? rows : [])
    .filter(row => {
      const isActive = readField(row, "isActive", "IsActive") !== false;
      const status = String(readField(row, "status", "Status") || "").toUpperCase();
      return isActive && status === "CONNECTED";
    })
    .map(row => ({
      phoneNumberId: String(readField(row, "phoneNumberId", "PhoneNumberId") || "").trim(),
      whatsAppBusinessNumber: readField(row, "whatsAppBusinessNumber", "WhatsAppBusinessNumber"),
      senderDisplayName: readField(row, "senderDisplayName", "SenderDisplayName"),
      verifiedName: readField(row, "verifiedName", "VerifiedName"),
      qualityRating: readField(row, "qualityRating", "QualityRating"),
      messagingLimitTier: readField(row, "messagingLimitTier", "MessagingLimitTier"),
      nameStatus: readField(row, "nameStatus", "NameStatus"),
      status: "CONNECTED",
      isDefault: readField(row, "isDefault", "IsDefault") === true,
    }));

const legacySummaryNumber = summary => {
  if (String(summary?.status || "").toUpperCase() !== "CONNECTED") return [];
  if (!summary?.phoneNumberId && !summary?.whatsAppBusinessNumber) return [];
  return [{
    phoneNumberId: summary.phoneNumberId || "",
    whatsAppBusinessNumber: summary.whatsAppBusinessNumber,
    senderDisplayName: null,
    verifiedName: summary.verifiedName,
    qualityRating: summary.qualityRating,
    messagingLimitTier: summary.messagingLimitTier,
    nameStatus: summary.nameStatus,
    status: "CONNECTED",
    isDefault: true,
  }];
};

const withConnectedNumbers = (summary, rows) => {
  const connectedNumbers = normalizeConnectedNumbers(rows);
  return {
    ...summary,
    connectedNumbers: connectedNumbers.length > 0
      ? connectedNumbers
      : legacySummaryNumber(summary),
  };
};

const qualityPresentation = value => {
  const raw = String(value || "").trim().toUpperCase();
  return QUALITIES[raw] || QUALITIES.UNKNOWN;
};

const fetchNumberRows = async () => {
  const response = await axiosClient.get("/whatsappsettings/META_CLOUD/numbers");
  return response.data;
};

export default function ConnectionSummaryCard({
  showBillingTimeZone = false,
  canManage = true,
  onBillingTimeZoneStatusChange,
}) {
  const [data, setData] = useState(null);
  const [billingTimeZone, setBillingTimeZone] = useState(null);
  const [billingTimeZoneError, setBillingTimeZoneError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasAutoRefreshed = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const [summaryResult, numbersResult, settingsResult] = await Promise.allSettled([
        axiosClient.get("whatsappsettings/connection-summary"),
        fetchNumberRows(),
        showBillingTimeZone
          ? axiosClient.get("/whatsappsettings/me", { __silent: true })
          : Promise.resolve(null),
      ]);
      if (!mounted.current) return;
      const summaryResponse = summaryResult.status === "fulfilled" ? summaryResult.value : null;
      if (summaryResponse?.data?.success) {
        const rows = numbersResult.status === "fulfilled" ? numbersResult.value : [];
        setData(withConnectedNumbers(summaryResponse.data.data, rows));
      }
      if (showBillingTimeZone) {
        if (settingsResult.status === "fulfilled") {
          const body = settingsResult.value?.data;
          const settings = body?.hasSettings === false ? null : body?.data ?? body;
          const status = settings?.wabaPricingTimeZoneStatus || "Missing";
          setBillingTimeZone({ id: settings?.wabaPricingTimeZoneId, status });
          onBillingTimeZoneStatusChange?.(status);
        } else {
          setBillingTimeZoneError("Billing timezone could not be loaded. Use the Account Summary refresh icon to retry.");
        }
      }
    } catch (err) {
      console.error("Failed to fetch connection summary", err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [showBillingTimeZone, onBillingTimeZoneStatusChange]);

  const handleRefresh = useCallback(async ({ silent = false } = {}) => {
    if (!canManage) return;
    setRefreshing(true);
    setBillingTimeZoneError("");
    try {
      const [summaryResult, timezoneResult] = await Promise.allSettled([
        axiosClient.post("whatsappsettings/connection-summary/refresh"),
        showBillingTimeZone
          ? axiosClient.post("/whatsappsettings/pricing-timezone/refresh", {}, { __silent: true })
          : Promise.resolve(null),
      ]);
      if (!mounted.current) return;
      let timezoneFailed = false;
      if (showBillingTimeZone) {
        const result = timezoneResult.status === "fulfilled" ? timezoneResult.value?.data : null;
        if (result?.success) {
          setBillingTimeZone({ id: result.timeZoneId, status: result.status });
          onBillingTimeZoneStatusChange?.(result.status);
        } else {
          timezoneFailed = true;
          const failure = timezoneResult.status === "rejected" ? timezoneResult.reason : null;
          setBillingTimeZoneError(failure?.response?.data?.message || result?.message ||
            "Billing timezone could not be refreshed. Use the Account Summary refresh icon to retry.");
        }
      }
      if (summaryResult.status === "rejected") throw summaryResult.reason;
      const res = summaryResult.value;
      if (!res.data.success) throw new Error(res.data.message || "Failed to refresh status.");
      if (res.data.success) {
        let rows = [];
        try {
          rows = await fetchNumberRows();
        } catch (numberError) {
          console.error("Failed to refresh connected WhatsApp numbers", numberError);
        }
        if (!mounted.current) return;
        setData(withConnectedNumbers(res.data.data, rows));
        if (!silent) {
          if (timezoneFailed) toast.warning("Account status updated. Billing timezone could not be refreshed; please retry.");
          else toast.success("Account summary updated from Meta.");
        }
      }
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.message || err.message || "Failed to refresh status.");
      } else {
        console.error("Auto refresh of connection summary failed", err);
      }
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, [showBillingTimeZone, onBillingTimeZoneStatusChange, canManage]);

  useEffect(() => {
    onBillingTimeZoneStatusChange?.(null);
    void fetchSummary();
  }, [fetchSummary, onBillingTimeZoneStatusChange]);

  useEffect(() => {
    const missingTimeZone = showBillingTimeZone && billingTimeZone && billingTimeZone.status !== "Configured";
    if (canManage && !loading && !refreshing && data && (!data.lastUpdated || missingTimeZone) && !hasAutoRefreshed.current) {
      hasAutoRefreshed.current = true;
      void handleRefresh({ silent: true });
    }
  }, [loading, refreshing, data, handleRefresh, canManage, showBillingTimeZone, billingTimeZone]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 animate-pulse">
        <div className="mb-4 h-6 w-1/3 rounded bg-slate-100" />
        <div className="space-y-3">
          <div className="h-16 rounded-xl bg-slate-100" />
          <div className="h-16 rounded-xl bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const connectedNumbers = data.connectedNumbers || [];
  const lastUpdated = data.lastUpdated ? dayjs(data.lastUpdated).fromNow() : "Never";

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-4 py-3 sm:px-6 sm:py-4">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <Activity size={16} className="text-indigo-500" />
          Account Summary
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-medium text-slate-400">Updated {lastUpdated}</span>
          {canManage && <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg border border-transparent p-1.5 text-slate-400 transition-all hover:border-slate-200 hover:bg-white hover:text-indigo-600 hover:shadow-sm disabled:cursor-not-allowed"
            title="Refresh from Meta"
            aria-label="Refresh account summary from Meta"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin text-indigo-500" : ""} />
          </button>}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {connectedNumbers.length > 0 ? (
          <div role="table" aria-label="Connected WhatsApp numbers">
            <div
              role="row"
              className={`sr-only border-b border-slate-100 px-2 pb-2 lg:not-sr-only lg:grid lg:items-center lg:gap-3 ${CONNECTED_NUMBER_GRID}`}
            >
              <span role="columnheader" className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Phone Number</span>
              <span role="columnheader" className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Display Name</span>
              <span role="columnheader" className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Status</span>
              <span role="columnheader" className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Quality</span>
              <span role="columnheader" className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Messaging Limit</span>
              <span role="columnheader" className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Default</span>
            </div>
            <div role="rowgroup" className="divide-y divide-slate-100">
            {connectedNumbers.map((number, index) => {
              const preferredDisplayName = number.senderDisplayName || number.verifiedName;
              const displayName = preferredDisplayName || "WhatsApp number";
              const quality = qualityPresentation(number.qualityRating);
              const key = number.phoneNumberId || number.whatsAppBusinessNumber || `number-${index}`;
              return (
                <div
                  key={key}
                  role="row"
                  data-testid={`connection-summary-number-${key}`}
                  className={`grid grid-cols-2 items-center gap-x-3 gap-y-2 px-2 py-3 lg:gap-3 ${CONNECTED_NUMBER_GRID}`}
                >
                  <div role="cell" className="col-span-2 overflow-hidden whitespace-nowrap text-xs font-semibold text-slate-600 lg:col-span-1">
                    {number.whatsAppBusinessNumber || "—"}
                  </div>
                  <div role="cell" className="col-span-2 min-w-0 lg:col-span-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="min-w-0 flex-1 truncate whitespace-nowrap text-xs font-bold text-slate-900"
                        title={preferredDisplayName ? displayName : undefined}
                      >
                        {displayName}
                      </span>
                      {number.nameStatus === "APPROVED" && (
                        <ShieldCheck size={14} className="shrink-0 text-emerald-500" aria-label="Verified by Meta" />
                      )}
                    </div>
                  </div>
                  <div role="cell">
                    <span className="inline-flex whitespace-nowrap items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Connected
                    </span>
                  </div>
                  <div role="cell">
                    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg border px-1.5 py-1 text-[10px] font-bold ${quality.color}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${quality.dot}`} />
                      {quality.label}
                    </span>
                  </div>
                  <div role="cell" className="whitespace-nowrap text-[10px] font-bold text-slate-700">
                    {formatMessagingLimitTier(number.messagingLimitTier)}
                  </div>
                  <div role="cell" className="text-left lg:text-center">
                    <span className={number.isDefault
                      ? "inline-flex rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700"
                      : "text-xs text-slate-300"}
                    >
                      {number.isDefault ? "Default" : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
            No connected WhatsApp numbers.
          </p>
        )}
        {showBillingTimeZone && billingTimeZone && (
          <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
            Billing timezone: <span className="font-medium text-slate-700">{billingTimeZone.id || "Not configured"}</span>
            {billingTimeZone.status !== "Configured" && (
              <p className="mt-1 text-amber-800">
                {canManage
                  ? "Billing setup is incomplete. Use the Account Summary refresh icon to retry syncing from Meta."
                  : "Billing setup is incomplete. Ask an account administrator to refresh Account Summary."}
              </p>
            )}
          </div>
        )}
        {billingTimeZoneError && <p role="alert" className="mt-3 text-xs text-rose-700">{billingTimeZoneError}</p>}
      </div>
    </div>
  );
}
